import type { CSSProperties, FC, ReactElement } from "react"

import eq from "lodash-es/eq"
import isEmpty from "lodash-es/isEmpty"

import { DefaultAvatarIcon, GuestIcon } from "@/3514/assets"
import { buildCssClassName } from "@/3514/utils"

import { ETypographyColor, ETypographySize, ETypographyTag, ETypographyWeight, Typography } from "../typography"

import { EAvatarFallbackVariant, EAvatarSize, type TAvatarProps } from "./avatar.types"

type _TImgProps = Partial<TAvatarProps> & { styles: CSSProperties }
type _TDefaultProps = Partial<TAvatarProps>
type _TConstructedProps = Partial<TAvatarProps> & {
    styles: CSSProperties
}

const Img: FC<_TImgProps> = ({ alt, size, url: src, className, styles }: _TImgProps): ReactElement => (
    <img
        loading="lazy"
        decoding="async"
        width={size}
        height={size}
        alt={alt}
        src={src}
        style={styles}
        className={className}
    />
)

const NoImage: FC<_TDefaultProps> = ({ alt, size }: _TDefaultProps): ReactElement => (
    <DefaultAvatarIcon width={size} height={size} alt={alt} />
)

const Constructed: FC<_TConstructedProps> = ({
    alt,
    size,
    className,
    userData,
    styles,
    isExtraParty = false,
    isRestCounter = false,
    fallbackVariant = EAvatarFallbackVariant.BlueColored
}: _TConstructedProps): ReactElement => {
    const _isGuest: boolean = isEmpty(userData?.firstName) || isEmpty(userData?.lastName)

    return (
        <div
            title={alt}
            className={buildCssClassName(className, { "outline outline-[1px] outline-blue-400": _isGuest })}
            style={styles}
        >
            {_isGuest || isExtraParty ? (
                <GuestIcon
                    variant={fallbackVariant === EAvatarFallbackVariant.BlueColored ? "blue200" : "accent"}
                    size={((): number => {
                        if (size === EAvatarSize._25) return 25
                        if (size === EAvatarSize._45) return 45

                        return 40
                    })()}
                />
            ) : (
                <Typography
                    text={`${userData?.firstName?.charAt(0)}${isRestCounter ? userData?.lastName : userData?.lastName?.charAt(0)}`}
                    weight={ETypographyWeight.Bold}
                    tag={ETypographyTag.Span}
                    size={eq(size, EAvatarSize._45) ? ETypographySize.Small : ETypographySize.Tiny}
                    color={
                        eq(fallbackVariant, EAvatarFallbackVariant.BlueColored)
                            ? ETypographyColor.Blue200
                            : ETypographyColor.White
                    }
                />
            )}
        </div>
    )
}
const helpers = { Img, NoImage, Constructed }

export { helpers as avatarHelpers }
